import { RefObject } from 'react';
import { FetchApiResponse } from '@api/fetchApi';
import { IBaseAccountSettings } from '@api/services/user/copyTrading/userCopyTrading.apiService.interfaces';
import { EnumUTAFormTAField } from '@components-member/TradingAccounts/AccountCreate/enums';
import { IInvestorAccountDetails } from '@components-member/TradingAccounts/CopyTradingAccounts/InvestorSettingsForm';
import { EnumUserSharedFormField } from '@components-member/shared/forms/enums';
import { FormUtils } from '@components-shared/forms/forms.utils';
import { EmptyObject } from '@components-shared/shared.interfaces';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';

const defaultSocialTradingSettings: IBaseAccountSettings = {
  availableAllocationMethods: [],
  isMarginStopCopyLevelSupported: false,
  isLossStopCopyLevelSupported: false,
  isStopCopyViaMarginLevelSupported: false,
};

type ConfigureAccountSettingsArgs<T extends { name: string }> = {
  value?: number | null;
  fields: T[];
  form: RefObject<{ update: (field: T[]) => void }>;
  onChange: (name: string, value: string) => void;
  fetchSettings: () => Promise<FetchApiResponse<IBaseAccountSettings>>;
};

export const configureAccountSettings = async <T extends { name: string }>({
  value,
  fields,
  form,
  onChange,
  fetchSettings,
}: ConfigureAccountSettingsArgs<T>) => {
  if (!value) {
    onChange('copyType', '');

    resetAccountCreateForm(fields);
    form.current?.update(fields);

    return defaultSocialTradingSettings;
  }

  const settings = await fetchAccountSettings(fetchSettings);

  updateAccountCreateForm({ value, fields, settings });

  form.current?.update(fields);

  return settings;
};

const fetchAccountSettings = async (
  fetchSettings: () => Promise<FetchApiResponse<IBaseAccountSettings>>,
): Promise<IBaseAccountSettings> => {
  try {
    const { data } = await fetchSettings();

    return data;
  } catch (error) {
    handleError(keyMap.sharedErrorsFailedToGetSettings, error);

    return defaultSocialTradingSettings;
  }
};

const updateAccountCreateForm = <T extends { name: string }>({
  value,
  fields,
  settings,
}: {
  value?: number | null;
  fields: T[];
  settings: IBaseAccountSettings;
}) => {
  if (!value) {
    resetAccountCreateForm(fields);
  } else {
    const {
      availableAllocationMethods,
      isMarginStopCopyLevelSupported,
      isStopCopyViaMarginLevelSupported,
      isLossStopCopyLevelSupported,
    } = settings;
    const options = availableAllocationMethods.map((option: string) => ({ value: option }));

    FormUtils.updateFormFieldByName(EnumUTAFormTAField.copyType, { options, attributes: { disabled: false } }, fields);
    updateCopyLevelAccountFields({
      isMarginStopCopyLevelSupported,
      isLossStopCopyLevelSupported,
      isStopCopyViaMarginLevelSupported,
      fields,
    });
  }
};

export const updateCopyLevelAccountFields = <T extends { name: string }>({
  isLossStopCopyLevelSupported,
  isMarginStopCopyLevelSupported,
  isStopCopyViaMarginLevelSupported,
  fields,
}: {
  isLossStopCopyLevelSupported: boolean;
  isMarginStopCopyLevelSupported: boolean;
  isStopCopyViaMarginLevelSupported: boolean;
  fields: T[];
}) => {
  FormUtils.updateFormFieldByName(
    EnumUserSharedFormField.percentageLossStopCopyLevel,
    { isHidden: !isLossStopCopyLevelSupported },
    fields,
  );
  const isMarginCopyVisible = isMarginStopCopyLevelSupported && !isStopCopyViaMarginLevelSupported;

  FormUtils.updateFormFieldByName(
    EnumUserSharedFormField.marginStopCopyLevel,
    { isHidden: !isMarginCopyVisible },
    fields,
  );

  FormUtils.updateFormFieldByName(
    EnumUserSharedFormField.stopCopyViaMarginLevel,
    { isHidden: !isStopCopyViaMarginLevelSupported },
    fields,
  );
};

export const updateInvestorMarginLevelFields = <T extends { name: string }>({
  account,
  isMarginStopCopyLevelSupported,
  isStopCopyViaMarginLevelSupported,
  fields,
}: {
  account: IInvestorAccountDetails | EmptyObject;
  isMarginStopCopyLevelSupported: boolean;
  isStopCopyViaMarginLevelSupported: boolean;
  fields: T[];
}) => {
  const marginStopCopyValue = account.marginStopCopyLevel;
  const isMarginStopCopyShouldBeHidden = !marginStopCopyValue || !isMarginStopCopyLevelSupported;

  if (isMarginStopCopyShouldBeHidden) {
    return;
  }

  FormUtils.updateFormFieldByName(
    EnumUserSharedFormField.marginStopCopyLevel,
    { isHidden: isMarginStopCopyShouldBeHidden },
    fields,
  );

  FormUtils.updateFormFieldByName(
    EnumUserSharedFormField.stopCopyViaMarginLevel,
    { isHidden: !isMarginStopCopyShouldBeHidden || !isStopCopyViaMarginLevelSupported },
    fields,
  );
};

const resetAccountCreateForm = <T extends { name: string }>(fields: T[]) => {
  FormUtils.updateFormFieldByName(EnumUTAFormTAField.copyType, { options: [], attributes: { disabled: true } }, fields);
  FormUtils.updateFormFieldByName(EnumUserSharedFormField.percentageLossStopCopyLevel, { isHidden: true }, fields);
  FormUtils.updateFormFieldByName(EnumUserSharedFormField.marginStopCopyLevel, { isHidden: true }, fields);
  FormUtils.updateFormFieldByName(EnumUserSharedFormField.stopCopyViaMarginLevel, { isHidden: true }, fields);
};
